import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { flushSync } from "react-dom";

function clamp(value: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, precision = 3): number {
  const p = 10 ** precision;
  return Math.round(value * p) / p;
}

function adjust(value: number, fromMin: number, fromMax: number, toMin: number, toMax: number): number {
  return toMin + ((value - fromMin) * (toMax - toMin)) / (fromMax - fromMin);
}

function pointerFromCenter(x: number, y: number): number {
  return clamp(Math.sqrt((y - 50) ** 2 + (x - 50) ** 2) / 50, 0, 1);
}

type UseHoloCardPointerOptions = {
  idleShimmer?: boolean;
  initialOpacity?: number;
  tiltDivisor?: number;
  flipRotateY?: number;
  lockFlipRotation?: boolean;
};

export function useHoloCardPointer({
  idleShimmer = false,
  initialOpacity = 0.42,
  tiltDivisor = 3.5,
  flipRotateY = 0,
  lockFlipRotation = false,
}: UseHoloCardPointerOptions = {}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const rotatorRef = useRef<HTMLDivElement>(null);
  const pointerActiveRef = useRef(false);
  const didDragRef = useRef(false);
  const interactingRef = useRef(false);
  const initialOpacityRef = useRef(initialOpacity);
  const tiltDivisorRef = useRef(tiltDivisor);
  const flipRotateYRef = useRef(flipRotateY);
  const lockFlipRotationRef = useRef(lockFlipRotation);
  const [interacting, setInteracting] = useState(false);

  const setInteractingOn = useCallback(() => {
    if (interactingRef.current) return;
    interactingRef.current = true;
    setInteracting(true);
  }, []);

  useEffect(() => {
    initialOpacityRef.current = initialOpacity;
    tiltDivisorRef.current = tiltDivisor;
  }, [initialOpacity, tiltDivisor]);

  useEffect(() => {
    lockFlipRotationRef.current = lockFlipRotation;
  }, [lockFlipRotation]);

  useEffect(() => {
    flipRotateYRef.current = flipRotateY;
    const card = cardRef.current;
    if (!card || interactingRef.current || lockFlipRotationRef.current) return;
    card.style.setProperty("--rotate-x", `${round(flipRotateY)}deg`);
  }, [flipRotateY]);

  const setFlipRotateY = useCallback((deg: number) => {
    flipRotateYRef.current = deg;
    const card = cardRef.current;
    if (!card) return;
    if (interactingRef.current) return;
    card.style.setProperty("--rotate-x", `${round(deg)}deg`);
  }, []);

  const applyIdleStyle = useCallback((card: HTMLDivElement) => {
    if (lockFlipRotationRef.current) return;
    card.style.setProperty("--pointer-x", "50%");
    card.style.setProperty("--pointer-y", "50%");
    card.style.setProperty("--pointer-from-center", "0");
    card.style.setProperty("--pointer-from-top", "0.5");
    card.style.setProperty("--pointer-from-left", "0.5");
    card.style.setProperty("--background-x", "50%");
    card.style.setProperty("--background-y", "50%");
    card.style.setProperty("--rotate-x", `${round(flipRotateYRef.current)}deg`);
    card.style.setProperty("--rotate-y", "0deg");
    card.style.setProperty("--card-opacity", String(initialOpacityRef.current));
    card.style.setProperty("--card-scale", "1");
    card.style.setProperty("--translate-x", "0px");
    card.style.setProperty("--translate-y", "0px");
  }, []);

  useEffect(() => {
    const card = cardRef.current;
    if (card) applyIdleStyle(card);
  }, [applyIdleStyle, initialOpacity]);

  const applyPointer = useCallback((clientX: number, clientY: number) => {
    const rotator = rotatorRef.current;
    const card = cardRef.current;
    if (!rotator || !card) return;

    const rect = rotator.getBoundingClientRect();
    const x = clamp(round((100 / rect.width) * (clientX - rect.left)));
    const y = clamp(round((100 / rect.height) * (clientY - rect.top)));
    const centerX = x - 50;
    const centerY = y - 50;

    card.style.setProperty("--pointer-x", `${x}%`);
    card.style.setProperty("--pointer-y", `${y}%`);
    card.style.setProperty("--pointer-from-center", String(round(pointerFromCenter(x, y))));
    card.style.setProperty("--pointer-from-top", String(round(y / 100)));
    card.style.setProperty("--pointer-from-left", String(round(x / 100)));
    card.style.setProperty("--background-x", `${round(adjust(x, 0, 100, 37, 63))}%`);
    card.style.setProperty("--background-y", `${round(adjust(y, 0, 100, 33, 67))}%`);
    card.style.setProperty("--rotate-x", `${round(-centerX / tiltDivisorRef.current + flipRotateYRef.current)}deg`);
    card.style.setProperty("--rotate-y", `${round(centerY / tiltDivisorRef.current)}deg`);
    card.style.setProperty("--card-opacity", "1");
    card.style.setProperty("--card-scale", "1");
    card.style.setProperty("--translate-x", "0px");
    card.style.setProperty("--translate-y", "0px");
  }, []);

  const resetPointer = useCallback(() => {
    const card = cardRef.current;
    flushSync(() => {
      interactingRef.current = false;
      setInteracting(false);
    });
    if (card) applyIdleStyle(card);
  }, [applyIdleStyle]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (idleShimmer) return;

      didDragRef.current = false;
      setInteractingOn();
      applyPointer(event.clientX, event.clientY);

      if (event.pointerType === "touch") {
        pointerActiveRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    },
    [applyPointer, idleShimmer],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (idleShimmer) return;
      if (event.pointerType === "touch" && !pointerActiveRef.current) return;

      if (event.pointerType === "touch" && pointerActiveRef.current) {
        didDragRef.current = true;
      }

      setInteractingOn();
      applyPointer(event.clientX, event.clientY);
    },
    [applyPointer, idleShimmer],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (idleShimmer) return;
      if (event.pointerType !== "touch") return;

      pointerActiveRef.current = false;

      if (didDragRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      resetPointer();
    },
    [idleShimmer, resetPointer],
  );

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (idleShimmer) return;

      pointerActiveRef.current = false;
      didDragRef.current = false;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      resetPointer();
    },
    [idleShimmer, resetPointer],
  );

  const onPointerLeave = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (idleShimmer) return;
      if (event.pointerType === "touch" && pointerActiveRef.current) return;

      resetPointer();
    },
    [idleShimmer, resetPointer],
  );

  useEffect(() => {
    if (idleShimmer) return;

    const handleLeaveWindow = () => {
      pointerActiveRef.current = false;
      didDragRef.current = false;
      resetPointer();
    };

    document.documentElement.addEventListener("mouseleave", handleLeaveWindow);
    window.addEventListener("blur", handleLeaveWindow);

    return () => {
      document.documentElement.removeEventListener("mouseleave", handleLeaveWindow);
      window.removeEventListener("blur", handleLeaveWindow);
    };
  }, [idleShimmer, resetPointer]);

  useEffect(() => {
    if (!idleShimmer) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let t = 0;
    const tick = () => {
      t += 0.04;
      const x = 50 + Math.sin(t) * 28;
      const y = 50 + Math.cos(t * 0.85) * 22;
      setInteractingOn();
      const card = cardRef.current;
      if (card) {
        card.style.setProperty("--pointer-x", `${round(x)}%`);
        card.style.setProperty("--pointer-y", `${round(y)}%`);
        card.style.setProperty("--pointer-from-center", String(round(pointerFromCenter(x, y))));
        card.style.setProperty("--pointer-from-top", String(round(y / 100)));
        card.style.setProperty("--pointer-from-left", String(round(x / 100)));
        card.style.setProperty("--background-x", `${round(adjust(x, 0, 100, 37, 63))}%`);
        card.style.setProperty("--background-y", `${round(adjust(y, 0, 100, 33, 67))}%`);
        card.style.setProperty("--rotate-x", `${round(-((x - 50) / tiltDivisorRef.current) + flipRotateYRef.current)}deg`);
        card.style.setProperty("--rotate-y", `${round((y - 50) / tiltDivisorRef.current)}deg`);
        card.style.setProperty("--card-opacity", "0.94");
        card.style.setProperty("--card-scale", "1");
        card.style.setProperty("--translate-x", "0px");
        card.style.setProperty("--translate-y", "0px");
      }
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [idleShimmer]);

  return {
    cardRef,
    rotatorRef,
    interacting,
    setFlipRotateY,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
  };
}
